import { useState } from "react";
import { Navigate, useLocation, useOutlet } from "react-router-dom";
import { AnimatePresence, useIsPresent } from "framer-motion";
import { FadeSlide } from "@/components/PageTransition";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { useApp } from "@/context/AppContext";
import { NAV, canAccess } from "@/lib/access";
import { RestrictedView } from "@/components/RestrictedView";
import { SiteFooter } from "@/components/SiteFooter";

export default function AppShell() {
  const { auth, user, loading, loggedOut } = useApp();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const outlet = useOutlet();
  const present = useIsPresent();

  if (auth === null || loading) return <div className="min-h-screen grid place-items-center text-gray-500" data-testid="app-loading">Loading EventHub…</div>;
  if (!auth) return present ? <Navigate to={loggedOut.current ? "/" : "/login"} replace /> : null;
  if (auth.kind === "member") return <Navigate to="/my" replace />;
  if (auth.must_change_password) return <Navigate to="/change-password" replace />;
  if (!user) return null;

  const current = NAV.find((n) => pathname.startsWith(n.path));
  const allowed = !current || canAccess(user, current.id);

  return (
    <div className="flex min-h-screen bg-[#F8F9FA]">
      <Sidebar collapsed={collapsed} mobileOpen={mobileOpen} onNavigate={() => setMobileOpen(false)} />
      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setMobileOpen(false)} data-testid="mobile-sidebar-overlay" />}
      <div className="flex-1 min-w-0 flex flex-col">
        <Header collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} onMobile={() => setMobileOpen(true)} />
        <main className="flex-1 px-4 sm:px-8 py-8 max-w-[1500px] w-full">
          <AnimatePresence mode="wait">
            <FadeSlide key={pathname}>{allowed ? outlet : <RestrictedView item={current} />}</FadeSlide>
          </AnimatePresence>
        </main>
        <SiteFooter />
      </div>
    </div>
  );
}
